import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// apicore
import { APICore } from "../../helpers/api/apiCore";

// helpers
import {
  getUsers as getUserList,
  createUserApi,
  getUser as getUserApi,
  updateUserApi,
  updateUserPasswordApi,
  deleteUserApi,
  downloadUserApi,
  updateUserActivationApi,
  updateUserProfileApi,
} from "../../helpers/";

// constants
import { UsersActionTypes } from "./constants";
import {
  createUserError,
  createUserSuccess,
  usersGetListError,
  usersGetListSuccess,
  getUserSuccess,
  getUserError,
  updateUserSuccess,
  updateUserError,
  updateUserPasswordSuccess,
  updateUserPasswordError,
  deleteUserSuccess,
  deleteUserError,
  usersDownloadSuccess,
  usersDownloadError,
  updateUserActivationSuccess,
  updateUserActivationError,
  updateUserProfileSuccess,
  updateUserProfileError,
  getSellerListSuccess,
  getSellerListError,
  getAgentListSuccess,
  getAgentListError,
  getDeliveryListSuccess,
  getDeliveryListError,
} from "./actions";
import { UserRole } from "../../constants/userRole";

interface UserData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    role?: string;
    country?: string;
    activated?: string;
  };
  type: string;
}

interface UserCreateData {
  payload: {
    name: string;
    role: UserRole;
    email: string;
    password: string;
    phone: string;
    country?: string;
    accountManager?: string | null;
    commissionRate?: number;
  };
  type: string;
}

interface UserUpdateData {
  payload: {
    id: string;
    name: string;
    role: UserRole;
    email: string;
    phone: string;
    packageId: string;
    isEmailVerified: boolean;
    isActivated: boolean;
    accountManager?: string | null;
    commissionRate?: number;
  };
  type: string;
}

interface UserDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface UserUpdatePasswordData {
  payload: {
    id: string;
    password: string;
  };
  type: string;
}

interface UserUpdateActivationData {
  payload: {
    id: string;
  };
  type: string;
}

interface GetUserData {
  payload: {
    id: string;
  };
  type: string;
}

interface UserProfileUpdateData {
  payload: {
    name: string;
    phone: string;
    rib?: string;
    cryptoWallet?: string;
    mobileMoney?: string;
  };
  type: string;
}

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - email and password
 */
function* getUsers({
  payload: { limit, skip, keyword, role, country, activated },
}: UserData): SagaIterator {
  try {
    const response = yield call(getUserList, {
      limit,
      skip,
      keyword: keyword ?? "",
      role: role ?? "",
      country: country ?? "",
      activated: activated ?? "",
    });
    if (response?.status === 403 || response?.__handled403) {
      yield put(usersGetListError(UsersActionTypes.GET_LIST_USERS, "Forbidden"));
      return;
    }
    const data = response.data;
    yield put(usersGetListSuccess(UsersActionTypes.GET_LIST_USERS, data));
  } catch (error: any) {
    yield put(usersGetListError(UsersActionTypes.GET_LIST_USERS, error));
  }
}

function* createUser({
  payload: { name, role, email, password, phone, country, accountManager, commissionRate },
}: UserCreateData): SagaIterator {
  try {
    const response = yield call(createUserApi, {
      name,
      role,
      email,
      password,
      phone,
      country,
      accountManager,
      commissionRate,
    });
    if (response?.status === 403 || response?.__handled403) {
      yield put(createUserError(UsersActionTypes.CREATE_USER, "You don't have permission to create this user"));
      return;
    }
    const user = response.data;
    yield put(createUserSuccess(UsersActionTypes.CREATE_USER, user));
  } catch (error: any) {
    const data = error?.response?.data;
    const errorMessage =
      typeof data === "string"
        ? data
        : data?.error || data?.message || error?.message || "An error occurred";
    yield put(createUserError(UsersActionTypes.CREATE_USER, errorMessage));
  }
}

function* updateUser({
  payload: {
    id,
    name,
    role,
    email,
    phone,
    packageId,
    isEmailVerified,
    isActivated,
    accountManager,
    commissionRate,
  },
}: UserUpdateData): SagaIterator {
  try {
    const response = yield call(updateUserApi, {
      id,
      name,
      role,
      email,
      phone,
      package: packageId,
      isEmailVerified,
      isActivated,
      ...(accountManager !== undefined ? { accountManager } : {}),
      ...(commissionRate !== undefined ? { commissionRate } : {}),
    });
    const user = response.data;
    yield put(updateUserSuccess(UsersActionTypes.UPDATE_USER, user));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(updateUserError(UsersActionTypes.UPDATE_USER, errorMessage));
  }
}

function* updateUserPassword({
  payload: { id, password },
}: UserUpdatePasswordData): SagaIterator {
  try {
    const response = yield call(updateUserPasswordApi, {
      id,
      password,
    });
    const user = response.data;
    yield put(
      updateUserPasswordSuccess(UsersActionTypes.UPDATE_USER_PASSWORD, user)
    );
  } catch (error: any) {
    const data = error?.response?.data;
    const errorMessage =
      typeof data === "string"
        ? data
        : data?.error || data?.message || error?.message || "An error occurred";
    yield put(
      updateUserPasswordError(
        UsersActionTypes.UPDATE_USER_PASSWORD,
        errorMessage
      )
    );
  }
}

function* updateUserActivation({
  payload: { id },
}: UserUpdateActivationData): SagaIterator {
  try {
    const response = yield call(updateUserActivationApi, {
      id,
    });
    const user = response.data;
    yield put(
      updateUserActivationSuccess(UsersActionTypes.UPDATE_USER_ACTIVATION, user)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      updateUserActivationError(
        UsersActionTypes.UPDATE_USER_ACTIVATION,
        errorMessage
      )
    );
  }
}

function* deleteUser({ payload: { ids } }: UserDeleteData): SagaIterator {
  try {
    const response = yield call(deleteUserApi, {
      ids,
    });
    const user = response.data;
    yield put(deleteUserSuccess(UsersActionTypes.DELETE_USER, user));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(deleteUserError(UsersActionTypes.DELETE_USER, errorMessage));
  }
}

function* getUser({ payload: { id } }: GetUserData): SagaIterator {
  try {
    const response = yield call(getUserApi, { id });
    const user = response.data;
    yield put(getUserSuccess(UsersActionTypes.GET_USER, user));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(getUserError(UsersActionTypes.GET_USER, errorMessage));
  }
}

function* downloadUsers({
  payload: { limit, skip, keyword, role, country },
}: UserData): SagaIterator {
  try {
    const response = yield call(downloadUserApi, {
      limit,
      skip,
      keyword,
      role,
      country,
    });
    const users = response.data;
    yield put(usersDownloadSuccess(UsersActionTypes.DOWNLOAD_USERS, users));
  } catch (error: any) {
    yield put(usersDownloadError(UsersActionTypes.DOWNLOAD_USERS, error));
  }
}

function* updateUserProfile({ payload }: UserProfileUpdateData): SagaIterator {
  try {
    const response = yield call(updateUserProfileApi, payload);
    const user = response.data;
    yield put(
      updateUserProfileSuccess(UsersActionTypes.UPDATE_USER_PROFILE, user)
    );
    const currentUser = api.getLoggedInUser();
    const updatedUser = {
      ...currentUser,
      name: user.name,
      email: user.email,
      phone: user.phone,
      rib: user?.rib,
      cryptoWallet: user?.cryptoWallet,
      mobileMoney: user?.mobileMoney,
      platformId: user?.platformId ?? currentUser?.platformId,
    };
    api.setLoggedInUser(updatedUser);
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response.data;
    yield put(
      updateUserProfileError(UsersActionTypes.UPDATE_USER_PROFILE, errorMessage)
    );
  }
}

function* getSellerList({
  payload: { limit, skip, keyword, country },
}: UserData): SagaIterator {
  try {
    const response = yield call(getUserList, {
      limit,
      skip,
      keyword,
      role: "seller",
      country,
    });
    if (response?.status === 403 || response?.__handled403) {
      yield put(getSellerListError(UsersActionTypes.GET_SELLER_LIST, "Forbidden"));
      return;
    }
    const users = response.data;
    yield put(getSellerListSuccess(UsersActionTypes.GET_SELLER_LIST, users));
  } catch (error: any) {
    yield put(getSellerListError(UsersActionTypes.GET_SELLER_LIST, error));
  }
}

function* getAgentList({
  payload: { limit, skip, keyword, country },
}: UserData): SagaIterator {
  try {
    const response = yield call(getUserList, {
      limit,
      skip,
      keyword,
      role: "agent",
      country,
    });
    if (response?.status === 403 || response?.__handled403) {
      yield put(getAgentListError(UsersActionTypes.GET_AGENT_LIST, "Forbidden"));
      return;
    }
    const users = response.data;
    yield put(getAgentListSuccess(UsersActionTypes.GET_AGENT_LIST, users));
  } catch (error: any) {
    yield put(getAgentListError(UsersActionTypes.GET_AGENT_LIST, error));
  }
}

function* getDeliveryList({
  payload: { limit, skip, keyword, country },
}: UserData): SagaIterator {
  try {
    const response = yield call(getUserList, {
      limit,
      skip,
      keyword,
      role: "delivery",
      country,
    });
    if (response?.status === 403 || response?.__handled403) {
      yield put(getDeliveryListError(UsersActionTypes.GET_DELIVERY_LIST, "Forbidden"));
      return;
    }
    const users = response.data;
    yield put(
      getDeliveryListSuccess(UsersActionTypes.GET_DELIVERY_LIST, users)
    );
  } catch (error: any) {
    yield put(getDeliveryListError(UsersActionTypes.GET_DELIVERY_LIST, error));
  }
}

export function* watchGetSellerList() {
  yield takeEvery(UsersActionTypes.GET_SELLER_LIST, getSellerList);
}

export function* watchGetAgentList() {
  yield takeEvery(UsersActionTypes.GET_AGENT_LIST, getAgentList);
}

export function* watchGetDeliveryList() {
  yield takeEvery(UsersActionTypes.GET_DELIVERY_LIST, getDeliveryList);
}

export function* watchUpdateUserProfile() {
  yield takeEvery(UsersActionTypes.UPDATE_USER_PROFILE, updateUserProfile);
}

export function* watchGetUsers() {
  yield takeEvery(UsersActionTypes.GET_LIST_USERS, getUsers);
}

export function* watchCreateUsers() {
  yield takeEvery(UsersActionTypes.CREATE_USER, createUser);
}

export function* watchUpdateUsers() {
  yield takeEvery(UsersActionTypes.UPDATE_USER, updateUser);
}

export function* watchDeleteUsers() {
  yield takeEvery(UsersActionTypes.DELETE_USER, deleteUser);
}

export function* watchUpdateUserPassword() {
  yield takeEvery(UsersActionTypes.UPDATE_USER_PASSWORD, updateUserPassword);
}

export function* watchUpdateUserActivation() {
  yield takeEvery(
    UsersActionTypes.UPDATE_USER_ACTIVATION,
    updateUserActivation
  );
}

export function* watchGetUser() {
  yield takeEvery(UsersActionTypes.GET_USER, getUser);
}

export function* watchDownloadUsers() {
  yield takeEvery(UsersActionTypes.DOWNLOAD_USERS, downloadUsers);
}

function* usersSaga() {
  yield all([
    fork(watchGetUsers),
    fork(watchGetSellerList),
    fork(watchGetAgentList),
    fork(watchGetDeliveryList),
    fork(watchCreateUsers),
    fork(watchGetUser),
    fork(watchUpdateUsers),
    fork(watchUpdateUserPassword),
    fork(watchUpdateUserActivation),
    fork(watchDeleteUsers),
    fork(watchDownloadUsers),
    fork(watchUpdateUserProfile),
  ]);
}

export default usersSaga;
